import type { PlanDraft } from "@weights/plan-reading";
import { useCallback, useEffect, useRef, useState } from "react";
import { AuthError } from "../../api/client.js";
import { onLive } from "../../live.js";
import { getPlan, isConflict, messageOf, savePlan, tryPlan } from "../api.js";
import { normalizeDraft as normalizeOnly } from "../model/draft.js";
import { stampDraft, takeReadings } from "../model/reading.js";
import { type History, type Step, emptyHistory, record, redo as redoStep, undo as undoStep } from "../model/history.js";
import { rebase } from "../model/merge.js";
import { type Change, applyChanges } from "../model/patch.js";
import type { Selection } from "../model/selection.js";

export const SAVE_DELAY = 800;

/** A draft from the server: its readings were made from its own text (§61.10). */
const normalizeDraft = (draft: PlanDraft, monday: string) => stampDraft(normalizeOnly(draft, monday));

export type SaveStatus =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "saved"; at: Date }
  | { kind: "error"; message: string; retrying: boolean };

export type Load = { kind: "loading" } | { kind: "missing" } | { kind: "error"; message: string } | { kind: "ready" };

interface Doc {
  draft: PlanDraft;
  /** What the server holds at `revision`. */
  saved: PlanDraft;
  revision: number;
  history: History;
  conflicts: Map<string, string[]>;
  /** The draft as last published from here (§17.5), for Changes since published. */
  published: PlanDraft | null;
  /** Kept for reference (§17.10): read only. */
  locked?: boolean;
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

export interface DraftDoc {
  load: Load;
  draft: PlanDraft | null;
  revision: number;
  status: SaveStatus;
  unsent: boolean;
  conflicts: Map<string, string[]>;
  published: PlanDraft | null;
  locked: boolean;
  canUndo: boolean;
  canRedo: boolean;
  undoLabel: string | null;
  redoLabel: string | null;
  commit: (changes: Change[], label: string, selection?: Selection) => void;
  /** Applied now, recorded as a step later (a definition typed into, one step per visit). */
  applyLive: (changes: Change[]) => void;
  recordStep: (step: Step) => void;
  undo: () => Step | null;
  redo: () => Step | null;
  setEditing: (editing: boolean) => void;
  flush: () => Promise<number>;
  retry: () => void;
  create: (draft: PlanDraft) => Promise<void>;
  reload: () => Promise<void>;
  resolveConflict: (key: string) => void;
  /** What the server holds now went out: it is the published version from here on. */
  markPublished: () => void;
  /** Counts the times another device's save changed what is shown (DESIGN §31.4). */
  remoteChanges: number;
}

/** The draft being edited, its undo stack, and autosave with the revision check (grid doc §4). */
export function useDraftDoc(monday: string, initial?: { draft: PlanDraft; revision: number; published?: PlanDraft | null }): DraftDoc {
  const [load, setLoad] = useState<Load>(initial ? { kind: "ready" } : { kind: "loading" });
  const [doc, setDocState] = useState<Doc | null>(() =>
    initial
      ? { draft: initial.draft, saved: initial.draft, revision: initial.revision, history: emptyHistory, conflicts: new Map(), published: initial.published ?? null }
      : null,
  );
  const [status, setStatus] = useState<SaveStatus>({ kind: "idle" });
  const [remoteChanges, setRemoteChanges] = useState(0);
  const hasInitial = !!initial;
  const docRef = useRef<Doc | null>(doc);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const editing = useRef(false);
  const wanted = useRef(false);
  const inFlight = useRef<Promise<void> | null>(null);
  const again = useRef(false);
  const failures = useRef(0);
  const alive = useRef(true);
  const loadRef = useRef<Load>(load);
  loadRef.current = load;
  /** A newer server draft announced while it couldn't be taken: its revision, or "check" after a reconnect. */
  const remote = useRef<number | "check" | null>(null);
  const taking = useRef(false);

  const setDoc = useCallback((next: Doc | null) => {
    docRef.current = next;
    setDocState(next);
  }, []);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      clearTimeout(timer.current);
      clearTimeout(retryTimer.current);
    };
  }, []);

  const fetchDoc = useCallback(async () => {
    if (hasInitial) return;
    setLoad({ kind: "loading" });
    try {
      const stored = await tryPlan(monday);
      if (!alive.current) return;
      if (!stored) {
        setDoc(null);
        setLoad({ kind: "missing" });
        return;
      }
      const draft = normalizeDraft(stored.draft, monday);
      const published = stored.published ? normalizeDraft(stored.published, monday) : null;
      setDoc({ draft, saved: draft, revision: stored.revision, history: emptyHistory, conflicts: new Map(), published, locked: !!stored.lockedAt });
      setStatus(stored.updatedAt ? { kind: "saved", at: new Date(stored.updatedAt) } : { kind: "idle" });
      setLoad({ kind: "ready" });
    } catch (error) {
      if (alive.current) setLoad({ kind: "error", message: messageOf(error) });
    }
  }, [monday, setDoc, hasInitial]);

  useEffect(() => {
    void fetchDoc();
  }, [fetchDoc]);

  /**
   * The server's draft taken in, our unsent edits redone on it (`rebase`). Unless `force`d (a
   * conflict), a revision we already have is left alone. Says whether what is shown changed.
   */
  const takeServer = useCallback(
    async (force: boolean): Promise<boolean> => {
      const stored = await tryPlan(monday);
      if (!alive.current) return false;
      const now = docRef.current;
      if (!stored) {
        if (!now && loadRef.current.kind === "missing") return false;
        setDoc(null);
        setLoad({ kind: "missing" });
        return true;
      }
      const theirs = normalizeDraft(stored.draft, monday);
      const locked = !!stored.lockedAt;
      if (!now) {
        const published = stored.published ? normalizeDraft(stored.published, monday) : null;
        setDoc({ draft: theirs, saved: theirs, revision: stored.revision, history: emptyHistory, conflicts: new Map(), published, locked });
        setStatus({ kind: "saved", at: new Date(stored.updatedAt) });
        setLoad({ kind: "ready" });
        return true;
      }
      if (!force && stored.revision <= now.revision) return false;
      const merged = rebase(now.saved, now.draft, theirs);
      const conflicts = new Map([...now.conflicts, ...merged.conflicts]);
      const published = stored.published ? normalizeDraft(stored.published, monday) : now.published;
      const changed = !same(merged.draft, now.draft);
      // Never undo across a server reload (grid doc §4): the steps were taken on another draft.
      const history = changed ? emptyHistory : now.history;
      setDoc({ draft: merged.draft, saved: theirs, revision: stored.revision, history, conflicts, published, locked });
      return changed;
    },
    [monday, setDoc],
  );

  const takeRemote = useRef<() => void>(() => {});

  const saveNow = useCallback((): Promise<void> => {
    if (inFlight.current) {
      again.current = true;
      return inFlight.current;
    }
    const current = docRef.current;
    if (!current || same(current.draft, current.saved)) return Promise.resolve();
    const sent = current.draft;
    const baseRevision = current.revision;
    setStatus({ kind: "saving" });
    const run = (async () => {
      try {
        const result = await savePlan(monday, sent, baseRevision);
        failures.current = 0;
        const now = docRef.current;
        if (now) {
          const took = takeReadings(sent, result.draft ? normalizeDraft(result.draft, monday) : undefined, now.draft);
          setDoc({ ...now, draft: took.draft, saved: took.saved, revision: result.revision });
        }
        setStatus({ kind: "saved", at: new Date() });
      } catch (error) {
        if (isConflict(error)) {
          try {
            await takeServer(true);
            again.current = true;
          } catch (inner) {
            setStatus({ kind: "error", message: messageOf(inner), retrying: false });
          }
          return;
        }
        failures.current++;
        const auth = error instanceof AuthError;
        setStatus({ kind: "error", message: messageOf(error), retrying: !auth });
        if (!auth) {
          clearTimeout(retryTimer.current);
          retryTimer.current = setTimeout(() => void saveNow(), Math.min(30000, 2000 * 2 ** Math.min(failures.current, 4)));
        }
      } finally {
        inFlight.current = null;
        if (again.current && alive.current) {
          again.current = false;
          void saveNow();
        } else if (alive.current) takeRemote.current();
      }
    })();
    inFlight.current = run;
    return run;
  }, [monday, setDoc, takeServer]);

  const schedule = useCallback(() => {
    clearTimeout(timer.current);
    if (editing.current) {
      wanted.current = true;
      return;
    }
    timer.current = setTimeout(() => void saveNow(), SAVE_DELAY);
  }, [saveNow]);

  const commit = useCallback(
    (changes: Change[], label: string, selection?: Selection) => {
      const current = docRef.current;
      if (!current || changes.length === 0) return;
      const step: Step = selection ? { label, changes, selection } : { label, changes };
      setDoc({ ...current, draft: applyChanges(current.draft, changes), history: record(current.history, step) });
      schedule();
    },
    [schedule, setDoc],
  );

  const applyLive = useCallback(
    (changes: Change[]) => {
      const current = docRef.current;
      if (!current || changes.length === 0) return;
      setDoc({ ...current, draft: applyChanges(current.draft, changes) });
      schedule();
    },
    [schedule, setDoc],
  );

  const recordStepCb = useCallback(
    (step: Step) => {
      const current = docRef.current;
      if (!current) return;
      setDoc({ ...current, history: record(current.history, step) });
    },
    [setDoc],
  );

  const undo = useCallback((): Step | null => {
    const current = docRef.current;
    if (!current) return null;
    const result = undoStep(current.history, current.draft);
    if (!result) return null;
    setDoc({ ...current, draft: result.draft, history: result.history });
    schedule();
    return result.step;
  }, [schedule, setDoc]);

  const redo = useCallback((): Step | null => {
    const current = docRef.current;
    if (!current) return null;
    const result = redoStep(current.history, current.draft);
    if (!result) return null;
    setDoc({ ...current, draft: result.draft, history: result.history });
    schedule();
    return result.step;
  }, [schedule, setDoc]);

  const setEditing = useCallback(
    (value: boolean) => {
      editing.current = value;
      if (value) clearTimeout(timer.current);
      else {
        if (wanted.current) {
          wanted.current = false;
          schedule();
        }
        takeRemote.current();
      }
    },
    [schedule],
  );

  const flush = useCallback(async (): Promise<number> => {
    clearTimeout(timer.current);
    for (let i = 0; i < 4; i++) {
      await saveNow();
      const current = docRef.current;
      if (!current) return 0;
      if (same(current.draft, current.saved) && !inFlight.current) return current.revision;
    }
    throw new Error("The week couldn't be saved. Try again.");
  }, [saveNow]);

  const retry = useCallback(() => {
    clearTimeout(retryTimer.current);
    void saveNow();
  }, [saveNow]);

  const create = useCallback(
    async (draft: PlanDraft) => {
      try {
        const result = await savePlan(monday, draft, 0);
        const took = takeReadings(draft, result.draft ? normalizeDraft(result.draft, monday) : undefined, draft);
        setDoc({ draft: took.draft, saved: took.saved, revision: result.revision, history: emptyHistory, conflicts: new Map(), published: null });
        setStatus({ kind: "saved", at: new Date() });
        setLoad({ kind: "ready" });
      } catch (error) {
        if (isConflict(error)) await fetchDoc();
        else throw error;
      }
    },
    [fetchDoc, monday, setDoc],
  );

  const reload = useCallback(async () => {
    const server = await getPlan(monday);
    const draft = normalizeDraft(server.draft, monday);
    const published = server.published ? normalizeDraft(server.published, monday) : null;
    setDoc({ draft, saved: draft, revision: server.revision, history: emptyHistory, conflicts: new Map(), published, locked: !!server.lockedAt });
    setStatus({ kind: "saved", at: new Date(server.updatedAt) });
  }, [monday, setDoc]);

  const resolveConflict = useCallback(
    (key: string) => {
      const current = docRef.current;
      if (!current) return;
      const conflicts = new Map(current.conflicts);
      conflicts.delete(key);
      setDoc({ ...current, conflicts });
    },
    [setDoc],
  );

  const markPublished = useCallback(() => {
    const current = docRef.current;
    if (current) setDoc({ ...current, published: current.saved });
  }, [setDoc]);

  takeRemote.current = () => {
    const wantedRevision = remote.current;
    if (wantedRevision === null || inFlight.current || editing.current || taking.current || !alive.current) return;
    if (loadRef.current.kind === "loading") return;
    remote.current = null;
    const now = docRef.current;
    if (typeof wantedRevision === "number" && wantedRevision > 0 && now && wantedRevision <= now.revision) return;
    taking.current = true;
    takeServer(false).then(
      (changed) => {
        taking.current = false;
        if (!alive.current) return;
        if (changed && docRef.current) setRemoteChanges((n) => n + 1);
        const after = docRef.current;
        if (after && !same(after.draft, after.saved)) schedule();
        takeRemote.current();
      },
      () => {
        taking.current = false;
        // A failed read waits for the next notice, or the next reconnect.
      },
    );
  };

  useEffect(() => takeRemote.current(), [load.kind]);

  useEffect(
    () =>
      onLive((event) => {
        if (event.kind === "reconnect") {
          if (remote.current === null) remote.current = "check";
        } else if (event.kind === "draft" && event.week === monday && typeof event.revision === "number") {
          const now = docRef.current;
          if (event.revision > 0 && now && event.revision <= now.revision) return;
          if (event.revision === 0 && !now && loadRef.current.kind === "missing") return;
          remote.current = event.revision;
        } else return;
        takeRemote.current();
      }),
    [monday],
  );

  const unsent = !!doc && (!same(doc.draft, doc.saved) || status.kind === "saving");

  useEffect(() => {
    if (!unsent) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [unsent]);

  const past = doc?.history.past ?? [];
  const future = doc?.history.future ?? [];
  return {
    load,
    draft: doc?.draft ?? null,
    revision: doc?.revision ?? 0,
    status,
    unsent,
    conflicts: doc?.conflicts ?? new Map(),
    published: doc?.published ?? null,
    locked: !!doc?.locked,
    canUndo: past.length > 0,
    canRedo: future.length > 0,
    undoLabel: past[past.length - 1]?.label ?? null,
    redoLabel: future[future.length - 1]?.label ?? null,
    commit,
    applyLive,
    recordStep: recordStepCb,
    undo,
    redo,
    setEditing,
    flush,
    retry,
    create,
    reload,
    resolveConflict,
    markPublished,
    remoteChanges,
  };
}
